/**
 * The till's live nudge.
 *
 * The agent opens an SSE stream on the loopback API and emits `changed`
 * whenever a projection moves — a sale here, an order pushed up from a kiosk, a
 * stock movement. A kiosk order used to take up to ten seconds to appear on the
 * counter because the till polled; now it arrives in about one.
 *
 * THE STREAM IS A NUDGE, NOT DATA. Every event carries an empty body on
 * purpose, and this module never hands anything but "something changed" to its
 * caller. The screen re-reads the endpoints it already trusts. A stream that
 * carried the order itself would be a second source of truth that can disagree
 * with the first, and the one it disagrees with is the one the cashier is
 * looking at.
 */

/**
 * The subset of EventSource this module uses.
 *
 * Narrow on purpose so a test can supply a fake: jsdom has no EventSource, and
 * a module that can only be exercised against a real browser is a module whose
 * reconnect behaviour never gets tested.
 */
export interface LiveStream {
  addEventListener(type: string, listener: () => void): void;
  close(): void;
  onerror: ((this: unknown, ev: unknown) => unknown) | null;
}

export type StreamFactory = (url: string) => LiveStream;

/**
 * connectLive calls onNudge whenever the agent says something changed, and once
 * on connect.
 *
 * The connect nudge is load-bearing rather than tidy: EventSource reconnects on
 * its own after a drop, and whatever changed while the socket was down produced
 * no event anybody received. Refreshing on every `ready` means a reconnect
 * reconciles instead of leaving the screen confidently stale — which is the
 * failure a cashier cannot see, because a wrong arrivals list looks exactly
 * like a quiet afternoon.
 *
 * Returns a function that closes the stream. Errors are deliberately NOT
 * surfaced: EventSource retries by itself, the caller keeps a slow poll for the
 * case where it never comes back, and a till that showed a red banner every
 * time the shop's wifi hiccuped would train its user to ignore banners.
 */
export function connectLive(
  url: string,
  onNudge: () => void,
  factory?: StreamFactory,
): () => void {
  const make: StreamFactory | undefined =
    factory ??
    (typeof EventSource !== 'undefined'
      ? (u: string) => new EventSource(u) as unknown as LiveStream
      : undefined);

  // No EventSource and no fake: the caller's poll is the whole mechanism. Not
  // an error — the till must keep working on any runtime that lacks it.
  if (!make) return () => {};

  let stream: LiveStream;
  try {
    stream = make(url);
  } catch {
    return () => {};
  }

  stream.addEventListener('ready', onNudge);
  stream.addEventListener('changed', onNudge);

  return () => {
    try {
      stream.close();
    } catch {
      /* closing a stream that is already gone is not a failure */
    }
  };
}

/**
 * liveUrl turns the API base into the event stream's address.
 *
 * Built from the same base as every other call so a till pointed at a different
 * agent streams from that agent, rather than from whatever host happens to be
 * serving the bundle.
 */
export function liveUrl(base: string): string {
  return `${base.replace(/\/$/, '')}/events`;
}
